import { goto } from '$app/navigation';
import { page } from '$app/stores';
import { AGE_GATING_AGE_GROUP } from '$lib/constants/persistent-store';
import { modalStore, redirectUrlStore } from '$lib/store';
import { SiteProvider } from '$lib/types/siteProvider';
import Logger from './logger';
import { PersistentStore } from './persistent-store';

/**
 * Determines how much times has passed since show has ended.
 * @param input
 * @returns [time] ago
 */
export const timeAgo = (time: string) => {
  const timePassed = new Date(new Date(time).getTime());
  const ONE_DAY = 1000 * 60 * 60 * 24;
  const ONE_HOUR = 1000 * 60 * 60;
  const ONE_MINUTE = 1000 * 60;
  const seconds = Math.floor(new Date().getTime() - timePassed.getTime());
  let interval = seconds / ONE_DAY;
  if (interval > 1) {
    return `${Math.floor(interval)} days ago`;
  }
  interval = seconds / ONE_HOUR;
  if (interval > 1) {
    return `${Math.floor(interval)} hours ago`;
  }
  interval = seconds / ONE_MINUTE;
  if (interval > 1) {
    return `${Math.floor(interval)} min ago`;
  }
  return `${Math.floor(seconds / 1000)} sec ago`;
};

/**
 * Creates a slug from a category.
 */
export const createSlug = (category: string) => {
  return category?.toLowerCase()?.replace(/ /g, '-');
};

/**This implementation uses a basic hashing algorithm known as the djb2 hash function. 
 * It iterates over each character in the payload, updates the hash value, and performs some bitwise operations to ensure it's a 32-bit integer.
 *  Finally, it returns the hash value as a hexadecimal string.

 * Keep in mind that this is a simple hash function and may not provide the same level of security or collision resistance as more advanced hashing algorithms.
 * If security is a concern, it's recommended to use a widely-tested cryptographic library.
*/
export const generateMD5Hash = (payload: string) => {
  let hash = 0;
  if (payload.length === 0) {
    return hash;
  }

  for (let i = 0; i < payload.length; i++) {
    const char = payload.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return hash.toString(16);
};

/**
 * Returns the path params from the current path.
 */
export const getPathParams = (path: string) => {
  if (path) return path.match(/\/[^/]+/g)?.map((param) => param.slice(1));
  return [];
};

/**
 * Extracts the text inside <p> tags from a string.
 * @param str - String to extract text from
 * @returns Array of strings
 */
export const extractTextInsidePTags = (str: string): string[] => {
  const trimmedStr = str.trim(); // Remove leading/trailing whitespace
  const matches = trimmedStr.match(/<p>(.*?)<\/p>/gs); // Match everything between <p> and </p> tags

  if (matches) {
    return matches.map((match) => {
      const textInsidePTags = match.replace(/<\/?p>/g, ''); // Remove <p> and </p> tags from the match
      return textInsidePTags;
    });
  }

  return [];
};
/**
 *
 * @param event Handle A11y warning
 * @param callback
 */
export function handleKey(event: KeyboardEvent, callback: (event: Event) => void) {
  if (event.key === 'Enter' || event.key === ' ') {
    callback(event);
  }
}

/**
 * Redirects to a given URL.
 */
export function redirectTo(url: string) {
  // if (url) window.location.href = url;
  // ! moving back to goto because the above is replace the url and resetting the state
  if (url) goto(url);
}

/**
 * Generate Random user id for games iframe link
 */
export function generateUUID() {
  const id = crypto.randomUUID();
  PersistentStore.set('nostra_userid', id);
  return id;
}

export const getAnonymousUuid = (): string => {
  let uuid = getCookieByName('a_uid');
  if (!uuid) {
    uuid = generateUUID();
    setCookie('a_uid', uuid, 365, '/', true, false, 'Strict');
  }
  return uuid;
};

export const getCookieByName = (name: string): string | null => {
  let cookie = '';
  if (typeof window !== 'undefined' && document.cookie) {
    cookie = document.cookie;
  }
  const cookies = cookie.split('; ').reduce(
    (acc, current) => {
      const [key, value] = current.split('=');
      acc[decodeURIComponent(key)] = decodeURIComponent(value || '');
      return acc;
    },
    {} as Record<string, string>
  );
  return cookies[name] || null;
};

/**
 * Sets a cookie with the specified name, value, and optional parameters.
 * @param name - The name of the cookie.
 * @param value - The value of the cookie.
 * @param days - The number of days until the cookie expires (default is 7 days).
 * @param path - The path for which the cookie is valid (default is '/').
 * @param secure - Specifies whether the cookie should only be sent over secure connections (default is true).
 * @param httpOnly - Specifies whether the cookie should be accessible only through HTTP requests (default is true).
 * @param sameSite - Specifies the SameSite attribute for the cookie (default is 'none').
 * @returns The generated cookie string.
 */
export const setCookie = (
  name: string,
  value: string,
  days = 7,
  path = '/',
  secure = true,
  httpOnly = true,
  sameSite = 'none'
): string => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  let cookieStr = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )};expires=${expires.toUTCString()};path=${path}`;

  if (secure) cookieStr += ';Secure';
  if (httpOnly) cookieStr += ';HttpOnly';
  cookieStr += `;SameSite=${sameSite}`;

  if (typeof window !== 'undefined') {
    document.cookie = cookieStr;
  }
  return cookieStr;
};

export function copyToClipboard(text: string) {
  navigator?.clipboard?.writeText(text).then(
    function () {
      Logger.log('Copying to clipboard was successful!');
    },
    function (err) {
      Logger.error('Could not copy text: ', err);
    }
  );
}

/**
 * @returns {boolean}
 * @description Check if the device is consuming a Samsung URL or not
 * @example portal.glance.com - true
 * @example mysites.glance.com - false
 */
export const handleSiteProvider = (): boolean => {
  let site = '';
  page.subscribe((value) => {
    site = value.url.hostname.split('.')[0];
  });
  return site === SiteProvider.SAMSUNG ? true : false;
  // return true;
};

export const calDiscountPercentage = (num: number, total: number): number => {
  return Math.round(((total - num) / total) * 100);
};

export const resizeGamesImages = (url: string, size: string) => {
  return `${url.split('?')[0]}?hub=true&image_type=bannerImageRoundedUrl&size=${size}`;
};

export const setModalStore = (url: string) => {
  if (PersistentStore.get(AGE_GATING_AGE_GROUP) === null) {
    modalStore.set({ isOpen: true });
    redirectUrlStore.set(url);
  } else {
    redirectTo(url);
  }
};
